/**
 * The /sessions URL state: which sessions, how many, and how far in.
 *
 * All of it lives in the URL rather than in component state, and that is
 * not a style preference — the route's loader refetches every 5 seconds
 * (`REFRESH_MS` in `routes/sessions.tsx`), and every loader run replaces
 * the loader data the page renders from. Paging state held in a
 * `useState` is not destroyed by that directly, but it immediately
 * disagrees with the rows beside it, and anything derived from the fetched
 * page is thrown away wholesale. `VaultAudit.tsx` is the worked example of
 * what that costs: it keeps its paged-in history in state and a
 * refresh-driven prop change discards it, so the 15s tick silently yanks
 * the operator back to the top page.
 *
 * In the URL it survives the tick, survives reload, and makes a filtered
 * view something an operator can paste into a ticket.
 *
 * Pure and React-free on purpose: these are the rules the page is read by,
 * they are worth testing without a DOM, and `validateSearch` needs them at
 * router-construction time.
 */
import { cursorTrail, trailParam } from './cursor-trail.ts'

/**
 * Page sizes offered in the picker.
 *
 * Not `DEFAULT_PAGE_SIZES` from `ui/Pagination.tsx` (25/50/100/250): 250
 * would be clamped to 200 by the server and the picker would then lie
 * about what it fetched, and a 10 is worth having here because a session
 * list is usually read alongside up to four live terminal tiles, which
 * leaves very little vertical room.
 */
export const SESSION_PAGE_SIZES: readonly number[] = [10, 25, 50, 100]

/**
 * The admin's page size when the URL does not name one.
 *
 * This is the ONLY place 25 is decided. The server deliberately has no
 * default page size — a bare `GET /sessions` returns every matching row,
 * because silently truncating a shared endpoint's answer is a trap for
 * the CLI and scripts that read it. So the operator-facing default lives
 * here, on the operator-facing client, and `routes/sessions.tsx` always
 * sends it as an explicit `limit`.
 */
export const DEFAULT_SESSION_PAGE_SIZE = 25

/**
 * The raw search params, exactly as they appear in the URL.
 *
 * Every field is optional and the defaults live in `resolveSessionsView`,
 * so the common view (live only, 25 rows, page 1) has a clean `/sessions`
 * URL with nothing in it.
 */
export interface SessionsSearch {
  /** The selected session, shown in the detail panel. Deliberately NOT a
   * loader dep — selection must not refetch the list, and the panel
   * fetches its session by id so it works even when the row is filtered
   * off the page (`VaultApprovalCard` deep-links finished sessions here). */
  session?: string
  /**
   * Show only this project's sessions — the `org_nodes` id of a project,
   * which is the same id Projects selects with `?sel=` and Vault with
   * `?node=`.
   *
   * It is how the Projects page's "Sessions for this project" link lands
   * here (see `lib/cross-links.ts`), and it is a loader dep: unlike
   * `session`, changing it changes which rows the server returns.
   */
  projectId?: string
  /**
   * Include inactive sessions. Absent means the default, which is live
   * only — so the URL only ever carries this when the operator has
   * explicitly asked for the finished ones.
   */
  live?: boolean
  pageSize?: number
  /**
   * The cursors used to reach this page, oldest first, comma-joined.
   *
   * The API's paging is cursor-based and forward-only: `before` is a row
   * id and there is no way to derive the cursor for page N without having
   * walked there. So the walk itself is what is stored — the trail is both
   * the "where am I" (its length) and the "how do I go back" (pop one).
   * Keeping a separate `page` number would be a second source of truth
   * that could disagree with the cursor beside it.
   *
   * Absent on page 1, which is the overwhelmingly common case.
   */
  cursors?: string
}

/** What the loader and the page actually need, with every default applied
 * and the page number derived rather than stored. */
export interface ResolvedSessionsView {
  /** True unless the operator ticked "include inactive". */
  liveOnly: boolean
  /** The project filter, or `undefined` for every project. No default:
   * arriving at `/sessions` with nothing in the URL means all of them. */
  projectId: string | undefined
  pageSize: number
  /** 1-based, matching what `Pagination` renders. */
  page: number
  /** The `before` cursor for this page; `undefined` on page 1. */
  before: string | undefined
  /** The trail itself, so prev/next can pop and push it. */
  trail: readonly string[]
}

/**
 * `validateSearch` for the route.
 *
 * Hostile input is dropped rather than rejected: a mangled `?pageSize=cat`
 * in a pasted link should land the operator on the default view, not an
 * error boundary. The server is the thing that validates — it answers 400
 * for a bad uuid or a non-positive limit — and this only has to stop the
 * admin from asking an obviously broken question.
 */
export function parseSessionsSearch(
  search: Record<string, unknown>,
): SessionsSearch {
  const parsed: SessionsSearch = {}
  if (typeof search.session === 'string') parsed.session = search.session
  // Not validated as a uuid here: the server answers 400 for a malformed
  // one, and this only has to stop the admin asking an obviously broken
  // question. An empty string is dropped so `?projectId=` reads as "no
  // filter" rather than "the project whose id is the empty string".
  if (typeof search.projectId === 'string' && search.projectId !== '')
    parsed.projectId = search.projectId
  // Only `false` is worth carrying: `live=true` is the default, so writing
  // it into the URL would put a param on every normal visit.
  if (search.live === false || search.live === 'false') parsed.live = false
  const pageSize = Number(search.pageSize)
  if (SESSION_PAGE_SIZES.includes(pageSize)) parsed.pageSize = pageSize
  if (typeof search.cursors === 'string' && search.cursors !== '')
    parsed.cursors = search.cursors
  return parsed
}

/** The trail helpers now live in `cursor-trail.ts` — the vault approvals
 * queue pages the same way, and one definition of "what a cursor trail is"
 * is the point. Re-exported because every existing importer and test takes
 * them from this path. */
export { cursorTrail, trailParam }

export function resolveSessionsView(
  search: SessionsSearch,
): ResolvedSessionsView {
  const trail = cursorTrail(search.cursors)
  return {
    // The one defaulted-to-true flag in the app. An operator opening
    // /sessions wants to know what is running now; the finished ones
    // accumulate forever and used to push the live work off the screen.
    liveOnly: search.live !== false,
    projectId: search.projectId,
    pageSize: search.pageSize ?? DEFAULT_SESSION_PAGE_SIZE,
    page: trail.length + 1,
    // `noUncheckedIndexedAccess` makes this `string | undefined` for free,
    // which is exactly right: an empty trail is page 1 and has no cursor.
    before: trail[trail.length - 1],
    trail,
  }
}
